import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import EmptyState from '@/components/base/EmptyState';
import useSeo from '@/hooks/useSeo';
import FilterBar from './components/FilterBar';
import {
  getArticlesByCategory,
  getArticlesByIds,
  getCategoryBySlug,
  getTopicBySlug,
} from '@/utils/data';

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { categoryName = '' } = useParams();
  const category = getCategoryBySlug(categoryName);
  const topic = getTopicBySlug(categoryName);

  const [selected, setSelected] = useState<Record<string, string>>({});
  const [sort, setSort] = useState('time');
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setSelected({});
    setSort('time');
    setVisible(PAGE_SIZE);
  }, [categoryName]);

  const title = category ? `色哟哟 - ${category.name} 娱乐资讯` : topic ? `${topic.title} - 色哟哟专题` : '色哟哟';
  useSeo({
    title,
    description: category?.desc || topic?.intro || '色哟哟多元娱乐资讯平台',
    keywords: `色哟哟,${category?.name || '专题'},娱乐资讯`,
  });

  const baseArticles = useMemo(() => {
    if (category) return getArticlesByCategory(category.slug);
    if (topic) return getArticlesByIds(topic.articleIds);
    return [];
  }, [category, topic]);

  const filtered = useMemo(() => {
    let list = baseArticles;
    if (category) {
      Object.entries(selected).forEach(([key, value]) => {
        if (value && value !== '全部') {
          list = list.filter((a) => a.filters[key] === value);
        }
      });
    }
    return [...list].sort((a, b) =>
      sort === 'hot'
        ? b.views - a.views
        : new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [baseArticles, selected, sort, category]);

  const shown = filtered.slice(0, visible);

  if (!category && !topic) {
    return (
      <div className="w-full px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-error-warning-line"
          title="未找到该分类"
          desc="你访问的分类或专题不存在，可能已被移除。"
          actionLabel="返回首页"
          linkTo="/"
        />
      </div>
    );
  }

  const headName = category ? category.name : topic!.title;
  const headDesc = category ? category.desc : topic!.intro;
  const crumbLabel = category ? category.name : '专题';

  const handleSelect = (key: string, value: string) => {
    setSelected((prev) => ({ ...prev, [key]: value }));
    setVisible(PAGE_SIZE);
  };

  return (
    <div className="w-full">
      <div className="border-b border-background-200 bg-background-100 px-4 py-6 md:px-8 md:py-8">
        <div className="mb-4">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: crumbLabel }]} />
        </div>
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
            <i className={`${category ? category.icon : 'ri-bookmark-line'} text-2xl`}></i>
          </span>
          <div>
            <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">{headName}</h1>
            <p className="mt-1 max-w-3xl text-sm text-foreground-500">{headDesc}</p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 py-8 md:px-8 md:py-10">
        {category && category.filters.length > 0 && (
          <div className="mb-6">
            <FilterBar
              groups={category.filters}
              selected={selected}
              onSelect={handleSelect}
              sort={sort}
              onSort={(v) => {
                setSort(v);
                setVisible(PAGE_SIZE);
              }}
              onReset={() => {
                setSelected({});
                setSort('time');
                setVisible(PAGE_SIZE);
              }}
            />
          </div>
        )}

        <div className="mb-5 flex items-center justify-between">
          <p className="text-sm text-foreground-500">
            共 <span className="font-semibold text-primary-600">{filtered.length}</span> 条资讯
          </p>
          <span className="hidden items-center gap-1 text-sm text-foreground-500 sm:flex">
            <i className="ri-sort-desc"></i>
            {sort === 'hot' ? '按热度排序' : '按时间排序'}
          </span>
        </div>

        {shown.length === 0 ? (
          <EmptyState
            icon="ri-search-eye-line"
            title="暂无相关资讯"
            desc="当前筛选条件下没有匹配的内容，试着调整或重置筛选条件吧。"
            actionLabel="重置筛选"
            onAction={() => {
              setSelected({});
              setVisible(PAGE_SIZE);
            }}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">
              {shown.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
            {visible < filtered.length && (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line"></i>
                  加载更多（还剩 {filtered.length - visible} 条）
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}